import { Flex, Row } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import type { RecoConfig } from '@melio/payments-recon-api-client-ts';
import Feedback from '@tools/ui/components/common/Feedback';
import Loading from '@tools/ui/components/common/Loading';
import { Typography } from '@tools/ui/dime';
import { RecoConfigContext } from '../../../components/config-context';
import { RECO_FEEDBACK_CHANNEL } from '../../../consts/reco.const';
import { useApiRequest } from '../../../hooks/useApiRequest';
import RecoStore from '../../../stores/recoStore';
import { RecordPanel } from './bank-record-panel/record-panel';
import DdaClosingStatusSidebar, {
  SIDEBAR_ANIMATION_DURATION_MS
} from './dda-gaps/dda-closing-status-sidebar';
import { QuickAccess } from './quick-access';

const Title = styled(Typography)`
  margin-bottom: 0 !important;
  font-family: 'PolySans', sans-serif;
`;

const Container = styled(Flex)`
  position: relative;
  max-width: 100%;
  height: 100%;
  padding: 24px 24px 30px;
  transition: all 250ms ease-in-out;
  overflow: hidden;
  flex: 1;
`;

const GlobalPaddingOverride = styled.div`
  margin-top: -24px;
  margin-left: -24px;
  width: calc(100% + 48px);
  height: calc(100% + 24px);
`;

interface Props extends React.PropsWithChildren {
  title: string;
}

let loadedConfig: RecoConfig | undefined = undefined;

export const RecoAppWrapper: React.FC<Props> = observer(
  ({ title, children }) => {
    const { data: recoConfig } = useApiRequest<RecoConfig>({
      method: 'get',
      path: `/recon/reco-config`,
      skip: !!loadedConfig
    });

    const isVisible = RecoStore.isClosingStatusSidebarVisible;
    const [shouldRenderSidebar, setShouldRenderSidebar] = useState(isVisible);

    useEffect(() => {
      if (isVisible) {
        setShouldRenderSidebar(true);
      } else {
        const timer = setTimeout(
          () => setShouldRenderSidebar(false),
          SIDEBAR_ANIMATION_DURATION_MS
        );
        return () => clearTimeout(timer);
      }
    }, [isVisible]);

    loadedConfig ||= recoConfig;
    if (!loadedConfig) return <Loading />;

    return (
      <RecoConfigContext.Provider value={loadedConfig}>
        <RecordPanel />

        <GlobalPaddingOverride>
          <Flex>
            <Container vertical={true} gap={16}>
              <Row align="middle" justify="space-between">
                <Title variant="heading1Bold">{title}</Title>

                <QuickAccess />
              </Row>

              {children}
            </Container>

            {!!shouldRenderSidebar && <DdaClosingStatusSidebar isClosing={!isVisible} />}
          </Flex>
        </GlobalPaddingOverride>

        <Feedback slackChannelId={RECO_FEEDBACK_CHANNEL} />
      </RecoConfigContext.Provider>
    );
  }
);
